<template>
  <div>
    <vx-card>
      <!-- Search and Filters -->
      <div class="flex items-center gap-4 mb-4">
        <vs-input
          class="flex-1"
          label-placeholder="Search by VM title, ID, or hostname"
          @keyup.enter="getAllVms()"
          v-model="search"
        >
          <template #icon>
            <i class="bx bx-search"></i>
          </template>
        </vs-input>
        <vs-button
          size="large"
          @click="getAllVms()"
          icon="search"
        >
          Search
        </vs-button>
        <vs-button
          color="primary"
          @click="showFilters = !showFilters"
          icon="filter_list"
        >
          Filters
        </vs-button>
      </div>

      <!-- Advanced Filters -->
      <div v-if="showFilters" class="mb-4 p-4 border rounded">
        <div class="grid grid-cols-3 gap-4">
          <vs-input
            label-placeholder="User ID"
            v-model="filters.user_id"
          />
          <vs-input
            label-placeholder="VM Status (4=Online, 5=Shutdown)"
            v-model="filters.vm_status"
          />
          <vs-checkbox v-model="filters.with_bandwidth">
            Only VMs with Bandwidth Package
          </vs-checkbox>
        </div>
      </div>

      <!-- Pagination -->
      <vs-pagination :total="countPage" v-model="pageNumber"></vs-pagination>

      <!-- VM Table -->
      <vs-table
        v-model="selectedVm"
        :data="vmList"
        @dblSelection="doubleSelection"
      >
        <template slot="thead">
          <vs-th>Title</vs-th>
          <vs-th>VM ID</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>CPU/RAM/HDD</vs-th>
          <vs-th>Public IP</vs-th>
          <vs-th>Bandwidth Package</vs-th>
          <vs-th>Datacenter</vs-th>
          <vs-th>User</vs-th>
          <vs-th>Created At</vs-th>
          <vs-th>Actions</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              {{ tr.vm_title || tr.vm_sequence_id }}
            </vs-td>
            <vs-td>
              <span class="font-mono text-sm">{{ tr.vm_sequence_id }}</span>
            </vs-td>
            <vs-td>
              <vs-chip :color="getStatusColor(tr.vm_status)">
                {{ getStatusText(tr.vm_status) }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <span class="text-xs">
                {{ tr.cpu }}C / {{ tr.ram }}GB / {{ tr.hdd }}GB
              </span>
            </vs-td>
            <vs-td>
              {{ tr.public_ip || '-' }}
            </vs-td>
            <vs-td>
              <div v-if="tr.bandwidth_package">
                <vs-chip color="primary" class="text-xs">
                  {{ tr.bandwidth_package.title }}
                </vs-chip>
                <p class="text-xs text-gray-500 mt-1">
                  Expires: {{ tr.bandwidth_package.expired_at }}
                </p>
              </div>
              <span v-else class="text-gray-400">-</span>
            </vs-td>
            <vs-td>
              {{ tr.datacenter_name }}
            </vs-td>
            <vs-td>
              <vs-button
                size="small"
                icon="account_box"
                :to="`/user?id=${tr.user_id}`"
              ></vs-button>
            </vs-td>
            <vs-td>
              {{ tr.created_at }}
            </vs-td>
            <vs-td>
              <vs-dropdown vs-custom-content vs-trigger-click>
                <vs-button
                  size="small"
                  class="btn-drop"
                  type="filled"
                  icon="expand_more"
                ></vs-button>

                <vs-dropdown-menu>
                  <vs-dropdown-item
                    v-if="tr.bandwidth_package"
                    @click="openMoveBandwidthModal(tr)"
                  >
                    <div class="flex items-center gap-2">
                      <i class="bx bx-transfer"></i>
                      <span>Move Bandwidth Package</span>
                    </div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="viewVmDetails(tr)">
                    <div class="flex items-center gap-2">
                      <i class="bx bx-info-circle"></i>
                      <span>View Details</span>
                    </div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="rebootPrompt(tr.id)">
                    <div class="flex items-center gap-2 text-warning">
                      <i class="bx bx-revision"></i>
                      <span>Reboot</span>
                    </div>
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="shutdownPrompt(tr.id)">
                    <div class="flex items-center gap-2 text-danger">
                      <i class="bx bx-power-off"></i>
                      <span>Shutdown</span>
                    </div>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <!-- Pagination Bottom -->
      <vs-pagination
        class="my-3"
        :total="countPage"
        v-model="pageNumber"
      ></vs-pagination>
    </vx-card>

    <!-- Move Bandwidth Package Modal -->
    <move-bandwidth-package-modal
      :active.sync="moveBandwidthModalActive"
      :source-vm="selectedVmForBandwidthMove"
      :source-bandwidth-package="selectedVmBandwidthPackage"
      @success="handleBandwidthMoveSuccess"
    />

    <!-- Reboot Confirmation -->
    <vs-prompt
      @cancel="selectedActionId = null"
      @accept="rebootVm"
      :active.sync="rebootActive"
      title="Reboot VM"
    >
      <div class="con-exemple-prompt">
        Are you sure you want to <span class="text-warning font-bold">reboot</span> this VM?
      </div>
    </vs-prompt>

    <!-- Shutdown Confirmation -->
    <vs-prompt
      @cancel="selectedActionId = null"
      @accept="shutdownVm"
      :active.sync="shutdownActive"
      title="Shutdown VM"
    >
      <div class="con-exemple-prompt">
        Are you sure you want to <span class="text-danger font-bold">shutdown</span> this VM?
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import MoveBandwidthPackageModal from '@/components/cloud/MoveBandwidthPackageModal.vue'

export default {
  name: 'CloudVmManagement',
  components: {
    MoveBandwidthPackageModal
  },
  data() {
    return {
      search: '',
      showFilters: false,
      filters: {
        user_id: '',
        vm_status: '',
        with_bandwidth: false
      },
      countPage: 0,
      pageNumber: 1,
      selectedVm: null,
      vmList: [],
      selectedActionId: null,
      rebootActive: false,
      shutdownActive: false,
      moveBandwidthModalActive: false,
      selectedVmForBandwidthMove: {},
      selectedVmBandwidthPackage: null
    }
  },
  watch: {
    pageNumber() {
      this.getAllVms()
    },
    search() {
      this.pageNumber = 1
      this.getAllVms()
    },
    'filters.user_id'() {
      this.pageNumber = 1
      this.getAllVms()
    },
    'filters.vm_status'() {
      this.pageNumber = 1
      this.getAllVms()
    },
    'filters.with_bandwidth'() {
      this.pageNumber = 1
      this.getAllVms()
    }
  },
  methods: {
    async getAllVms() {
      this.$vs.loading()
      
      try {
        const params = {
          filter: this.search,
          page: this.pageNumber
        }

        if (this.filters.user_id) {
          params.user_id = this.filters.user_id
        }

        if (this.filters.vm_status) {
          params.vm_status = this.filters.vm_status
        }

        const response = await this.$http.get('admin/cloud/vm', { params })

        if (response.data.status) {
          this.countPage = response.data.params.last_page
          this.vmList = response.data.params.data

          // Filter by bandwidth package if needed
          if (this.filters.with_bandwidth) {
            this.vmList = this.vmList.filter(vm => vm.bandwidth_package)
          }
        }
      } catch (error) {
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در بارگذاری سرورها',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },

    openMoveBandwidthModal(vm) {
      this.selectedVmForBandwidthMove = vm
      this.selectedVmBandwidthPackage = vm.bandwidth_package
      this.moveBandwidthModalActive = true
    },

    handleBandwidthMoveSuccess(data) {
      this.$vs.notify({
        title: 'موفق',
        text: `پکیج پهنای باند "${data.bandwidth_package_title}" از سرور ${data.source_vm_id} به سرور ${data.target_vm_id} منتقل شد`,
        color: 'success'
      })
      this.moveBandwidthModalActive = false
      this.getAllVms()
    },

    viewVmDetails(vm) {
      // Navigate to VM details page or open modal
      this.$router.push(`/cloud/vm/${vm.id}`)
    },

    rebootPrompt(id) {
      this.selectedActionId = id
      this.rebootActive = true
    },

    shutdownPrompt(id) {
      this.selectedActionId = id
      this.shutdownActive = true
    },

    async rebootVm() {
      try {
        const response = await this.$http.get(`admin/cloud/vm/${this.selectedActionId}/reboot`)
        
        this.$vs.notify({
          title: 'موفق',
          text: response.data.message || 'ریبوت سرور آغاز شد',
          color: 'success'
        })
        
        this.getAllVms()
      } catch (error) {
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در ریبوت سرور',
          color: 'danger'
        })
      } finally {
        this.selectedActionId = null
      }
    },

    async shutdownVm() {
      try {
        const response = await this.$http.get(`admin/cloud/vm/${this.selectedActionId}/shutdown`)
        
        this.$vs.notify({
          title: 'موفق',
          text: response.data.message || 'خاموش کردن سرور آغاز شد',
          color: 'success'
        })
        
        this.getAllVms()
      } catch (error) {
        this.$vs.notify({
          title: 'خطا',
          text: (error.response && error.response.data && error.response.data.message) || 'خطا در خاموش کردن سرور',
          color: 'danger'
        })
      } finally {
        this.selectedActionId = null
      }
    },

    doubleSelection() {
      if (this.selectedVm) {
        this.$router.push(`/user?id=${this.selectedVm.user_id}`)
      }
    },

    getStatusColor(status) {
      const statusColors = {
        1: 'dark',      // Creating
        2: 'warning',   // Starting
        3: 'primary',   // Stopping
        4: 'success',   // Online
        5: 'danger',    // Shutdown
        6: 'warning',   // Rebooting
        7: 'primary',   // Reinstalling
        8: 'dark',      // Deleted
        9: 'danger'     // Error
      }
      return statusColors[status] || 'dark'
    },

    getStatusText(status) {
      const statusText = {
        1: 'Creating',
        2: 'Starting',
        3: 'Stopping',
        4: 'Online',
        5: 'Shutdown',
        6: 'Rebooting',
        7: 'Reinstalling',
        8: 'Deleted',
        9: 'Error'
      }
      return statusText[status] || 'Unknown'
    }
  },
  created() {
    this.getAllVms()
  }
}
</script>

<style scoped>
.vs-table >>> .vs-table--tbody-table tr td {
  padding: 10px 5px !important;
}
</style>
